import { RotateCcw, Send, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { CancelRequisitionSlipModal } from '../components/CancelRequisitionSlipModal';
import { RequisitionSlipModal } from '../components/RequisitionSlipModal';
import { requisitionSlipsApi } from '../requisitionSlipsApi';
import { RIS_STAGE } from '../requisitionSlipTypes';
import type { CurrentRequester, RequisitionSlipListItem } from '../requisitionSlipTypes';

/**
 * The requester's own requisition slips.
 *
 * **IT LISTS ONLY WHAT THIS ACCOUNT RAISED, and nothing here says so to the server.** The
 * scope is fixed by the route — there is no scope parameter on the wire, so nothing this page
 * sends can widen it.
 *
 * **There is no Clear data**, and the absence is its own decision: emptying a register of what
 * was asked for and what was approved destroys the accountability record — the call
 * `stock-movements`, `issuances` and `real-properties` already take.
 */
export function RequisitionSlipsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<RequisitionSlipListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore' | 'submit';
    row: RequisitionSlipListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [requester, setRequester] = useState<CurrentRequester | null>(null);

  /*
    CREATE IS WITHHELD from an account with no employee record, because the server refuses the
    slip anyway — the requesting office is derived from that record, and a slip with no office
    cannot be routed. The sentence is the server's own, from the same resolve the create runs.

    Until `/me` answers, and if it never does, Create stays enabled: the server is the authority,
    and a button disabled on a failed lookup would lock out an account that is linked.
  */
  useEffect(() => {
    const controller = new AbortController();

    requisitionSlipsApi
      .me(controller.signal)
      .then(setRequester)
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      requisitionSlipsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          facility: query.facets.facility?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: RequisitionSlipListItem): MenuItem[] => {
      const items: MenuItem[] = [];

      if (row.isActive && row.stage === RIS_STAGE.draft) {
        items.push({
          key: 'submit',
          label: 'Submit for endorsement',
          icon: <Send className="h-4 w-4" aria-hidden="true" />,
          permission: 'requisition-slips.submit',
          onClick: () => setConfirming({ kind: "submit", row }),
        });
      }

      /*
        CANCEL is offered on every stage the server permits, which is Draft, Submitted and
        Approved. In phase 1 nothing releases stock, so Approved is where a slip stops and
        withdrawing is the only way out of it.
      */
      if (row.isActive && row.stage !== RIS_STAGE.rejected && row.stage !== RIS_STAGE.cancelled) {
        items.push({
          key: 'cancel',
          label: 'Withdraw slip',
          icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
          permission: 'requisition-slips.cancel',
          onClick: () => setCancelling(row),
        });
      }

      if (!row.isActive) {
        items.push({
          key: 'restore',
          label: 'Restore',
          icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
          permission: 'requisition-slips.delete',
          onClick: () => setConfirming({ kind: "restore", row }),
        });
      }

      return items;
    },
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'submit') {
        await requisitionSlipsApi.submit(confirming.row.id);
      } else if (confirming.kind === 'restore') {
        await requisitionSlipsApi.restore(confirming.row.id);
      } else {
        await requisitionSlipsApi.remove(confirming.row.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('That did not work.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<RequisitionSlipListItem>
        module="requisition-slips"
        breadcrumb={['Self Service', 'Inventory', 'Requisition and Issue Slip']}
        columns={COLUMNS}
        facets={[
          { key: 'stage', label: 'Stage' },
          { key: 'facility', label: 'Store asked' },
          { key: 'status', label: 'Status' },
        ]}
        /*
          Filed by STAGE rather than by the row's live-or-deleted state: the question a
          requester opens this with is how far their slips have got, not which of them they
          deleted.
        */
        folderNavFacet="stage"
        /*
          BOTH entity types. A module whose list names only the parent is blind to every write
          to its lines — silently, because the general Audit Log page still shows them.
        */
        auditEntityTypes={['RequisitionSlip', 'RequisitionSlipItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by slip number, office or purpose"
        emptyMessage="No requisition slips yet. Raise one to ask Logistics for supplies your office needs."
        /*
          A refusal on the ACCOUNT is announced, not only explained on hover (CLAUDE.md). The
          banner and the disabled Create carry the server's own sentence; seeded met, so a lookup
          that never answers locks nobody out.
        */
        prerequisites={[
          {
            label: 'your employee record',
            met: requester?.error == null,
            detail: requester?.error ?? undefined,
            to: '/personnel',
            permission: 'personnel.update',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="requisition-slips.create"
        onRowOpen={(row) => navigate(`/self-service/requisition-slips/${row.id}`)}
        openPermission="requisition-slips.read"
        rowActions={rowActions}
        otherInfo={(row) => row}
      />

      {creating && (
        <RequisitionSlipModal
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            navigate(`/self-service/requisition-slips/${saved.id}`);
          }}
        />
      )}

      {cancelling && (
        <CancelRequisitionSlipModal
          slipNumber={cancelling.slipNumber}
          slipId={cancelling.id}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            refresh();
          }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          open
          id="requisition-slip-confirm-dialog"
          title={
            confirming.kind === 'submit'
              ? `Submit ${confirming.row.slipNumber}`
              : confirming.kind === 'restore'
                ? `Restore ${confirming.row.slipNumber}`
                : `Delete ${confirming.row.slipNumber}`
          }
          danger={confirming.kind === 'delete'}
          /*
            EVERY ONE OF THE THREE CONFIRMS, and submit states what leaves the requester's
            hands — the record then moves in somebody else's inbox, so a stray click reaches
            past this page. `requireConfirm`, never a plain two-click dialog: two clicks in the
            same place is a gesture dismissed on reflex.

            NO `requireCredential` on any of them: raising and withdrawing a slip are ordinary
            acts of the job, and ceremony on something done weekly is ceremony people learn to
            click through. The DELETE is the one that could take it, and does not for the same
            reason — nothing is destroyed.
          */
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                {confirming.kind === 'submit'
                  ? 'This hands the slip to its approver and takes it out of draft, so its lines and details can no longer be corrected. Getting it back is a withdrawal and a fresh slip, not an undo.'
                  : confirming.kind === 'restore'
                    ? 'The slip comes back into your register with its number and its history intact.'
                    : 'Deleting is not withdrawing. The slip is kept and can be restored; withdrawing it is a decision the record keeps, with a reason. Use that if the office has changed its mind.'}
              </p>
              {error ? <p className="text-red-700">{error.message}</p> : null}
            </div>
          }
          loading={running}
          onConfirm={runConfirmed}
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
        />
      )}
    </>
  );
}

const COLUMNS: ColumnDef<RequisitionSlipListItem>[] = [
  {
    /*
      THE SLIP NUMBER is the anchor — what a requisition is called and quoted by, and unlike
      the item catalog's SKU there is no competing name for the row.

      The PURPOSE rides with it, which is the two-value anchor the purchase-request register
      already takes: a column of `RIS-26-00004` says which slip without saying what it is for,
      so a reader had to open one to find out. ONE line, ONE header, ONE width, joined by the
      ` — ` this product joins a qualifier to a name with, with the number in the mono face and
      the prose in the body face.
    */
    key: 'slipNumber',
    header: 'Slip number',
    width: 320,
    sortable: true,
    render: (row) => <TextCell code={row.slipNumber} value={row.purpose} clamp />,
  },
  {
    key: 'storageFacilityName',
    header: 'Store asked',
    width: 180,
    summary: true,
    render: (row) => <TextCell value={row.storageFacilityName} />,
  },
  {
    key: 'lineCount',
    header: 'Lines',
    width: 90,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={String(row.lineCount)} mono numeric />,
  },
  {
    key: 'totalQuantity',
    header: 'Requested',
    width: 120,
    sortable: true,
    /*
      IT MIXES UNITS and is tolerable only because it is a COUNT of things asked for rather
      than a quantity of anything — it sits beside the line count so the two read together, and
      it is never promoted to a figure anybody acts on.
    */
    render: (row) => <TextCell value={String(row.totalRequestedQuantity)} mono numeric />,
  },
  {
    /*
      THE LIFECYCLE, beside the row's own live-or-deleted pill. Two state columns deliberately:
      a deleted slip and a withdrawn one are different facts, and a reader must not have to
      guess which the column is reporting.

      The SHELL places it immediately before `status`, from the `stage` flag, so the table and
      the list view cannot disagree about which column is which. The LABEL is the server's.
    */
    key: 'stage',
    header: 'Stage',
    width: 140,
    sortable: true,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.stageLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) => (
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>
    ),
  },
];
